export const TAU = Math.PI * 2
export const DEG = Math.PI / 180
export const RAD = 180 / Math.PI

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Inverse lerp, clamped to 0..1. */
export function invLerp(a: number, b: number, v: number): number {
  return a === b ? 0 : clamp01((v - a) / (b - a))
}

export function smoothstep(edge0: number, edge1: number, v: number): number {
  const t = invLerp(edge0, edge1, v)
  return t * t * (3 - 2 * t)
}

export function smootherstep(edge0: number, edge1: number, v: number): number {
  const t = invLerp(edge0, edge1, v)
  return t * t * t * (t * (t * 6 - 15) + 10)
}

/**
 * Framerate-independent exponential approach. `rate` is roughly "how many
 * e-foldings per second"; higher is snappier.
 */
export function damp(current: number, target: number, rate: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-rate * dt))
}

/** Signed shortest angular difference, in (-PI, PI]. */
export function angleDelta(from: number, to: number): number {
  let d = (to - from) % TAU
  if (d > Math.PI) d -= TAU
  if (d <= -Math.PI) d += TAU
  return d
}

export function dampAngle(current: number, target: number, rate: number, dt: number): number {
  return current + angleDelta(current, target) * (1 - Math.exp(-rate * dt))
}

/** Move `current` toward `target` by at most `maxDelta`. */
export function moveTowards(current: number, target: number, maxDelta: number): number {
  const d = target - current
  if (Math.abs(d) <= maxDelta) return target
  return current + Math.sign(d) * maxDelta
}

/**
 * Falls to 0 outside [-1,1], peaks at 1 for v=0. Smooth. Used constantly by the
 * terrain sculpting to blend a feature in over a distance.
 */
export function bump(v: number): number {
  const a = Math.abs(v)
  if (a >= 1) return 0
  const t = 1 - a
  return t * t * (3 - 2 * t)
}
